import { ref } from 'vue';
import { isAxiosError } from 'axios';
import api from '@/shared/api';
import { useCustomToast, shouldShowErrorToast, extractAxiosBlobErrorMessage } from '@/shared/lib';

export interface RevisionFileData {
  id: number;
  docUrl?: string | null;
  minioObjectName?: string | null;
}

export interface RevisionFileRequest {
  path: string;
  params?: Record<string, number>;
}

// O backend emite os dois parâmetros da RFC 6266, nesta ordem:
// `attachment; filename="document-80.pptx"; filename*=UTF-8''document-80.pptx`.
// Cada padrão para no `;` ou na aspa de fechamento, em vez de ancorar no fim do
// header — era o `$` do regex anterior que fazia o nome engolir o `filename*`
// inteiro e chegar ao disco como `document-80.pptx_; filename_=UTF-8''...`.
// O `i` está aí porque nome de parâmetro é case-insensitive pela RFC 6266 — um
// `Filename=` de outro servidor cairia no fallback sem ele.
const FILENAME_STAR_PATTERN = /filename\*=\s*UTF-8''([^;]+)/i;
const FILENAME_PATTERN = /filename="([^"]*)"|filename=\s*([^;]+)/i;

/**
 * Lê o nome do arquivo de um cabeçalho `Content-Disposition`.
 *
 * Prefere o `filename*` percent-encoded ao `filename` ASCII: é o que a RFC 6266
 * manda, e é o único dos dois que carrega caractere fora do ASCII sem perda — o
 * `filename` traz `_` no lugar dele.
 *
 * @param contentDisposition - Cabeçalho da resposta, se veio
 * @param fileExtension - Extensão do objeto no storage, para o nome de fallback
 * @returns Nome do arquivo, ou `document.<extensão>` quando o header não nomeia
 */
const extractFilename = (
  contentDisposition: string | undefined,
  fileExtension: string | undefined
): string => {
  const fallbackFilename = `document.${fileExtension}`;

  if (!contentDisposition) {
    return fallbackFilename;
  }

  const encodedMatch = FILENAME_STAR_PATTERN.exec(contentDisposition);

  if (encodedMatch?.[1]) {
    const encodedFilename = encodedMatch[1].trim();

    try {
      return decodeURIComponent(encodedFilename);
    } catch {
      // Percent-encoding corrompido: o valor cru ainda é melhor que o fallback
      // genérico, porque pelo menos carrega o nome que o servidor quis dar.
      return encodedFilename;
    }
  }

  const plainMatch = FILENAME_PATTERN.exec(contentDisposition);
  const plainFilename = (plainMatch?.[1] ?? plainMatch?.[2])?.trim();

  return plainFilename ? plainFilename : fallbackFilename;
};

export default function useRevisionFileDelivery() {
  const toast = useCustomToast();
  const error = ref<string | null>(null);
  const isLoading = ref(false);

  const handleRevisionNotFound = () => {
    const errorMessage = 'Nenhuma revisão válida encontrada para este documento.';
    error.value = errorMessage;
    toast.showErrorToast({
      title: 'Revisão não encontrada',
      message: errorMessage,
      type: 'error',
    });
  };

  const handleFileNotAvailable = () => {
    const errorMessage = 'Nenhum arquivo ou URL disponível para este documento.';
    error.value = errorMessage;
    toast.showErrorToast({
      title: 'Arquivo não disponível',
      message: errorMessage,
      type: 'error',
    });
  };

  const handleDocumentError = async (err: unknown) => {
    const genericMessage = 'Não foi possível carregar o documento. Tente novamente mais tarde.';

    // The file endpoints answer with `responseType: 'blob'`, so a 403 or a 404 arrives as a Blob and
    // `err.response.data.message` is undefined -- the server's reason never reached the user, and a
    // permission denial read as "try again later". Reading the message out of the blob is what makes
    // the toast tell the truth about why the file did not open.
    const blobMessage = await extractAxiosBlobErrorMessage(err, genericMessage);
    const serverMessage =
      blobMessage ??
      (isAxiosError(err) && err.response?.data?.message
        ? (err.response.data.message as string)
        : undefined);

    // `error.value` fica com o texto mais específico que houver, inclusive o de um `Error` cru:
    // é estado interno, que nenhuma tela renderiza hoje.
    error.value = serverMessage ?? (err instanceof Error ? err.message : genericMessage);

    // O toast é o que o usuário lê, então só exibe mensagem do servidor (em pt-BR) ou a genérica —
    // nunca o texto cru de um `Error`, que é técnico e em inglês.
    // A 404 here means a document, revision or file that stopped existing between the listing and
    // the click; with no toast, clicking the eye shows nothing and reads as a broken button.
    if (shouldShowErrorToast(err) || (isAxiosError(err) && err.response?.status === 404)) {
      toast.showErrorToast({
        title: 'Erro ao buscar documento',
        message: serverMessage ?? genericMessage,
        type: 'error',
      });
    }
  };

  const openPdf = async (request: RevisionFileRequest) => {
    const response = await api.get(request.path, {
      responseType: 'blob',
      params: request.params,
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);

    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  };

  const downloadFile = async (request: RevisionFileRequest, fileExtension: string | undefined) => {
    const response = await api.get(request.path, {
      responseType: 'blob',
      params: request.params,
    });

    const contentDisposition = response.headers['content-disposition'];
    const dispositionString =
      typeof contentDisposition === 'string' ? contentDisposition : undefined;

    const filename = extractFilename(dispositionString, fileExtension);

    const contentType = response.headers['content-type'];
    const mimeType = typeof contentType === 'string' ? contentType : undefined;
    const blob = new Blob([response.data], { type: mimeType });
    const blobUrl = URL.createObjectURL(blob);

    const anchor = window.document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    window.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  };

  const deliverRevisionFile = async (
    revision: RevisionFileData | undefined,
    buildRequest: (endpoint: 'pdf' | 'download') => RevisionFileRequest,
    onExternalUrlOpened?: (revision: RevisionFileData) => Promise<void>
  ) => {
    if (!revision) {
      handleRevisionNotFound();
      return;
    }

    if (revision.docUrl) {
      window.open(revision.docUrl, '_blank');
      await onExternalUrlOpened?.(revision);
      return;
    }

    if (!revision.minioObjectName) {
      handleFileNotAvailable();
      return;
    }

    const fileExtension = revision.minioObjectName.split('.').pop();

    if (fileExtension === 'pdf') {
      await openPdf(buildRequest('pdf'));
      return;
    }

    await downloadFile(buildRequest('download'), fileExtension);
  };

  const runFileDelivery = async (deliver: () => Promise<void>) => {
    error.value = null;
    isLoading.value = true;

    try {
      await deliver();
    } catch (err) {
      await handleDocumentError(err);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    error,
    isLoading,
    deliverRevisionFile,
    runFileDelivery,
  };
}
